import React, { useState, useEffect } from 'react';
import AppLayout from '../../components/AppLayout';
import ProspectingNav from '../../components/prospecting/ProspectingNav';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Edit2, Play, Pause, Users, ArrowRight, MessageSquare, Mail, Clock, GitBranch } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STEP_ICONS = { linkedin_connection: GitBranch, linkedin_message: MessageSquare, email: Mail, wait: Clock };

export default function Sequences() {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchSequences(); }, []);

  const fetchSequences = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/prospecting/sequences`, { credentials: 'include' });
      if (res.ok) setSequences(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this sequence?')) return;
    await fetch(`${BACKEND_URL}/api/prospecting/sequences/${id}`, { method: 'DELETE', credentials: 'include' });
    fetchSequences();
  };

  const handlePause = async (id) => {
    await fetch(`${BACKEND_URL}/api/prospecting/sequences/${id}/pause`, { method: 'POST', credentials: 'include' });
    fetchSequences();
  };

  const handleResume = async (id) => {
    await fetch(`${BACKEND_URL}/api/prospecting/sequences/${id}/resume`, { method: 'POST', credentials: 'include' });
    fetchSequences();
  };

  const card = `${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'} border rounded-xl`;

  return (
    <AppLayout title="Sequences">
      <div className="space-y-6" data-testid="sequences-page">
        <ProspectingNav />
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Outreach Sequences</h1>
            <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Multi-step automated outreach campaigns</p>
          </div>
          <button onClick={() => navigate('/lead-finder/sequences/new')} data-testid="create-sequence-btn" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#A2FF00] text-black font-medium hover:bg-[#8FE600] text-sm">
            <Plus className="w-4 h-4" /> New Sequence
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#A2FF00]"></div></div>
        ) : sequences.length === 0 ? (
          <div className={`${card} p-12 text-center`}>
            <MessageSquare className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
            <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No sequences yet. Create your first outreach sequence.</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {sequences.map(seq => (
              <div key={seq.sequence_id} className={card} data-testid={`seq-${seq.sequence_id}`}>
                <div className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{seq.name}</h3>
                        <span className={`px-2 py-0.5 rounded text-xs ${seq.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                          {seq.is_active ? 'Active' : 'Paused'}
                        </span>
                      </div>
                      {seq.description && <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{seq.description}</p>}

                      {/* Steps preview */}
                      <div className="flex items-center gap-1 mt-3 flex-wrap">
                        {(seq.steps || []).map((step, i) => {
                          const Icon = STEP_ICONS[step.type] || Clock;
                          return (
                            <React.Fragment key={i}>
                              <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${isDark ? 'bg-white/10 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                                <Icon className="w-3 h-3" />
                                <span className="capitalize">{step.type.replace('_', ' ')}</span>
                                {step.delay_days > 0 && <span className="opacity-60">+{step.delay_days}d</span>}
                              </div>
                              {i < seq.steps.length - 1 && <ArrowRight className={`w-3 h-3 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />}
                            </React.Fragment>
                          );
                        })}
                      </div>

                      {/* Stats */}
                      <div className={`flex gap-4 mt-3 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        <span>Enrolled: {seq.stats?.leads_enrolled || 0}</span>
                        <span>Active: {seq.stats?.leads_active || 0}</span>
                        <span>Replied: {seq.stats?.leads_replied || 0}</span>
                        <span>Reply Rate: {((seq.stats?.avg_reply_rate || 0) * 100).toFixed(1)}%</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => navigate(`/lead-finder/sequences/${seq.sequence_id}`)} className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`} title="Edit"><Edit2 className="w-4 h-4" /></button>
                      {seq.is_active ? (
                        <button onClick={() => handlePause(seq.sequence_id)} className={`p-2 rounded-lg hover:bg-yellow-500/20 text-yellow-400`} title="Pause"><Pause className="w-4 h-4" /></button>
                      ) : (
                        <button onClick={() => handleResume(seq.sequence_id)} className={`p-2 rounded-lg hover:bg-green-500/20 text-green-400`} title="Resume"><Play className="w-4 h-4" /></button>
                      )}
                      <button onClick={() => handleDelete(seq.sequence_id)} className={`p-2 rounded-lg hover:bg-red-500/20 text-red-400`} title="Delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
